import React, { ReactElement, useEffect, useState } from 'react';
import {
  Pagination,
  withErrorBoundary,
  NoDataCard,
  EventEmitter
} from '@nile/components';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { Card, SkeletonBodyText } from '@superops/ui-design-system';
import { useGlobalCardCarouselStore } from './CardCarouselStore';
interface CardCarousel {
  id?: string;
  query: any;
  client: any;
  data: any;
  setData: (data: any) => void;
  cardComponent: ReactElement;
  defaultSort: any;
  defaultCondition: any;
  accessorKey: string;
  dataKey: string;
  additionalVariables: any;
  cardProps: any;
  emptyState?: any;
  headingComponent?: ReactElement;
  minWidth?: number;
  refreshEventEmitters?: string[];
}

const CardCarousel = (props: CardCarousel) => {
  const {
    id,
    query,
    client,
    data,
    setData,
    cardComponent,
    defaultSort,
    defaultCondition,
    accessorKey,
    dataKey,
    additionalVariables = {},
    cardProps = {},
    emptyState,
    headingComponent,
    minWidth = 350,
    refreshEventEmitters = []
  } = props;

  const [pageSize, setPageSize] = useState(null);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const DEFAULT_LIST_INFO = {
    page: 1,
    pageSize: 3,
    sort: defaultSort || null,
    condition: defaultCondition || null
  };

  const getFittableCardCount = React.useCallback(
    (containerWidth: number) => {
      if (!minWidth || !containerWidth) return DEFAULT_LIST_INFO.pageSize;

      const gutter = 20; // px
      const cardsThatFit = Math.floor(
        (containerWidth + gutter) / (minWidth + gutter)
      );
      const usedWidth = cardsThatFit * (minWidth + gutter) - gutter;
      const remaining = containerWidth - usedWidth;

      // If leftover space can fit another full card + gutter
      const totalCards =
        remaining >= minWidth + gutter ? cardsThatFit + 1 : cardsThatFit;
      return totalCards;
    },
    [minWidth]
  );

  const useStore = useGlobalCardCarouselStore({
    id: props?.id || 'card_carousel'
  });

  const listInfo = useStore((s) => s.listInfo);
  const setListInfo = useStore((s) => s.setListInfo);

  // const [listInfo, setListInfo] = useState(null);

  const [loading, setLoading] = useState(false);
  const CardComponent = cardComponent || null;

  const fetchData = async (variables: any) => {
    setLoading(true);
    const response = await client.query({
      query,
      variables,
      fetchPolicy: 'network-only'
    });
    const dataValue = response?.data?.[accessorKey]?.[dataKey];
    setData(dataValue);
    setListInfo(response?.data?.[accessorKey]?.listInfo);
    setLoading(false);
  };

  const navigatePages = (pageNumber: number) => {
    const variables = {
      listInfo: {
        sort: listInfo?.sort,
        page: pageNumber,
        pageSize: listInfo?.pageSize,
        condition: listInfo?.condition
      },
      ...additionalVariables
    };
    fetchData(variables);
  };

  const pages = React.useMemo(() => {
    return Math.ceil(listInfo?.totalCount / listInfo?.pageSize) || 0;
  }, [listInfo]);

  const HeadingComponent = headingComponent || <></>;

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    const listInfoData = listInfo || DEFAULT_LIST_INFO;
    const variables = {
      listInfo: {
        sort: listInfoData?.sort,
        page: listInfoData?.page,
        pageSize: newPageSize,
        condition: listInfoData?.condition
      },
      ...additionalVariables
    };
    fetchData(variables);
  };

  useEffect(() => {
    if (!minWidth || !containerRef.current) {
      handlePageSizeChange(DEFAULT_LIST_INFO.pageSize);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      const containerWidth = entry.contentRect.width;
      const newPageSize = getFittableCardCount(containerWidth);

      if (newPageSize && pageSize !== newPageSize) {
        handlePageSizeChange(newPageSize);
      }
    });

    observer.observe(containerRef.current);

    // Run once on mount
    const initialWidth = containerRef.current.offsetWidth;
    if (initialWidth && listInfo) {
      const newPageSize = getFittableCardCount(initialWidth);
      if (newPageSize && listInfo.pageSize !== newPageSize) {
        handlePageSizeChange(newPageSize);
      }
    }

    return () => observer.disconnect();
  }, [
    containerRef,
    getFittableCardCount,
    handlePageSizeChange,
    listInfo,
    pageSize
  ]);

  const refreshList = () => {
    const variables = {
      listInfo: {
        sort: listInfo?.sort,
        page: 1,
        pageSize: listInfo?.pageSize,
        condition: listInfo?.condition
      },
      ...additionalVariables
    };
    fetchData(variables);
  };

  useEffect(() => {
    const refreshEventEmittersList =
      refreshEventEmitters && Array.isArray(refreshEventEmitters)
        ? refreshEventEmitters
        : [];

    refreshEventEmittersList.forEach((event) => {
      EventEmitter.subscribe(event, refreshList);
    });

    return () => {
      refreshEventEmittersList.forEach((event) => {
        EventEmitter.unsubscribe(event);
      });
      // Unsubscribe from watchQuery if active
    };
  }, [refreshEventEmitters, fetchData]);

  return (
    <div
      className="card-carousel-wrapper "
      id="card-carousel"
      ref={containerRef}
    >
      <div className="flex fx-sb actr mb20">
        <div>
          <HeadingComponent listInfo={listInfo} />
        </div>
        {listInfo?.totalCount > 0 && (
          <Pagination
            onPageChange={navigatePages}
            rowCount={listInfo?.pageSize}
            recordCount={listInfo?.totalCount}
            pages={pages}
            page={listInfo?.page}
          />
        )}
      </div>

      {!isEmpty(data) ? (
        <>
          <div className="flex g20 actr w100">
            {data?.map((item) => {
              return (
                <div
                  className="w100 flex-item-balloon min-w0 f110 "
                  id="card-wrapper"
                >
                  <CardComponent data={item} loading={loading} {...cardProps} />
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {loading ? (
            <div className="flex g20 actr w100">
              {Array.from({ length: pageSize || 3 }).map((_, index) => (
                <div className="w100">
                  <Card padding="sizeSmall">
                    <div className="height300">
                      <SkeletonBodyText lines={3} />
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex actr jctr w100 mt40 mb40">
              <NoDataCard
                imageURL={`url(${emptyState?.nodataImage ||
                  `${envConfig.imageRoot}svg/not-found.svg`})`}
                text={emptyState?.heading}
                description={emptyState?.description}
                height="40px"
                width="40px"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default withErrorBoundary(CardCarousel);
